import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '~env';
import { Pageable } from '~shared/models/pageable.model';
import { User } from '~shared/models/general/user.model';
import { Role } from '~shared/models/permission-schema.model';
import { Observable } from 'rxjs';
import { BaseApiService } from '../base.api-service';

@Injectable({
  providedIn: 'root',
})
export class UserApiService extends BaseApiService {
  /**
   * Liste paginée des utilisateurs.
   *
   * Le tri part sous la clé `order_by`, la SEULE que le serveur lise :
   * `PaginationParam::fromParamFetcher()` va chercher `order_by`, et rien
   * d'autre. `orderBy` reste déclaré côté contrôleur pour ne pas casser les
   * appelants existants, mais n'est jamais lu — l'émettre revenait à ne
   * demander aucun tri, et la liste retombait silencieusement sur
   * l'identifiant.
   *
   * `orderBy` désigne ici une COLONNE SQL, pas la clé sérialisée : la clause
   * est posée par un QueryBuilder DBAL (`$qb->orderBy(...)`) sur la table
   * `user`. Trier sur la dernière modification demande donc `updated_at`, alors
   * que la réponse, elle, porte `updatedAt`.
   */
  find(
    limit: number,
    offset: number,
    orderBy: string | undefined,
    order: 'asc' | 'desc' | undefined,
    filters: unknown[]
  ) {
    let params = new HttpParams();

    if (limit) {
      params = params.append('limit', limit);
    }
    if (offset) {
      params = params.append('offset', offset);
    }
    if (orderBy) {
      params = params.append('order_by', orderBy);
    }
    if (order) {
      params = params.append('order', order);
    }
    if (filters) {
      params = params.append('filter', JSON.stringify(filters));
    }
    return this.http.get<Pageable<User>>(
      environment.apiBasePaths.general + '/user',
      { params }
    );
  }

  findById(id: number) {
    return this.http.get<User>(
      environment.apiBasePaths.general + `/user/${id}`
    );
  }

  /**
   * Récupère l'utilisateur courant (incluant ses rôles techniques).
   * Depuis le refactor du permission bundle, les rôles ne sont plus dans le
   * JWT : cet endpoint est la source de vérité pour déterminer l'accès admin.
   */
  me(): Observable<User> {
    return this.http.get<User>(environment.apiBasePaths.general + '/user/me');
  }

  create(datas: FormData) {
    return this.http.post(environment.apiBasePaths.general + '/user', datas, {
      headers: this.formDataHeaders(),
    });
  }

  update(id: number, datas: URLSearchParams): Observable<User> {
    return this.http.patch<User>(
      environment.apiBasePaths.general + `/user/${id}`,
      datas,
      {
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded',
        }),
      }
    );
  }

  deleteOne(id: number) {
    return this.http.delete(environment.apiBasePaths.general + `/user/${id}`);
  }

  emailExists(email: string): Observable<boolean> {
    const params = new HttpParams().set('email', email);
    return this.http.get<boolean>(
      environment.apiBasePaths.general + '/user/email-exist',
      { params }
    );
  }

  updateRoles(id: number, roles: Role[]): Observable<void> {
    return this.http.put<void>(
      environment.apiBasePaths.data + `/user/${id}/roles`,
      { roles }
    );
  }
}
